import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import {
  LayoutDashboard, Users, AlertTriangle, Activity, Bell,
  LogOut, Menu, X, Shield, Globe, Cpu
} from 'lucide-react';
import { Button } from './ui/button';

const navItems = [
  { path: '/admin', key: 'overview', icon: LayoutDashboard, exact: true },
  { path: '/admin/tenants', key: 'tenants', icon: Users },
  { path: '/admin/errors', key: 'errors', icon: AlertTriangle },
  { path: '/admin/performance', key: 'performance', icon: Activity },
  { path: '/admin/alerts', key: 'alerts', icon: Bell },
  { path: '/admin/pdpl', key: 'pdpl', icon: Shield },
  { path: '/admin/ai-models', key: 'aiModels', icon: Cpu },
];

export default function AdminLayout({ children }) {
  const { admin, logout } = useAdminAuth();
  const { t } = useTranslation();
  const { lang, toggleLanguage, dir } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isRtl = dir === 'rtl';

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-slate-100 flex" dir={dir}>
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 z-50 w-64 bg-[#0d1224] border-slate-800/60
        transform transition-transform duration-200 ease-in-out
        lg:relative lg:translate-x-0
        ${isRtl ? 'right-0 border-l' : 'left-0 border-r'}
        ${sidebarOpen ? 'translate-x-0' : (isRtl ? 'translate-x-full' : '-translate-x-full')}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b border-slate-800/60">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-sm text-white tracking-wide">{t('common.appName')}</h1>
                <p className="text-[10px] text-red-400 font-medium uppercase tracking-widest">Super Admin</p>
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-${item.key}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-all duration-150
                    ${active
                      ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }
                  `}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{t(`admin.nav.${item.key}`)}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-slate-800/60">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                <Shield className="w-4 h-4 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-300 truncate">{admin?.email}</p>
                <p className="text-[10px] text-red-400">Super Admin</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 me-2" />
              {t('admin.nav.logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-14 bg-[#0d1224]/80 backdrop-blur-xl border-b border-slate-800/60 flex items-center px-4 lg:px-6 sticky top-0 z-30">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden text-slate-400 me-3"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="admin-sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            data-testid="admin-lang-toggle"
            className="text-slate-400 hover:text-white me-3 gap-1.5"
          >
            <Globe className="w-4 h-4" />
            <span className="text-xs">{lang === 'ar' ? 'EN' : 'عربي'}</span>
          </Button>
          <Link to="/dashboard" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
            {t('admin.nav.backToClient')}
          </Link>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
